import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { copyUrlSearchParams, objectEqual } from "@ciq/shared/utils/object"
import { arrayEquals } from "@ciq/shared/utils/array"
import { useAppContext } from "@ciq/shared/hooks/context"
import { ZoneType } from "@ciq/shared/utils/const"

const DEFAULT_ARRAY = []

export const FilterIds = {
  Options: {
    SEMESTER: "semester",
    HOURLY: "hourly",
    HEATMAP: "heatmap",
    MORE: "more",
  },
}

export const DimensionDisplayType = {
  TWO_D: "2D",
  THREE_D: "3D",
}

export const GraphType = {
  BY_DAY: "by_day",
  BY_HOUR: "by_hour",
}

export const FilterQueryArgs = {
  OPTION: "mapOption",
  HOUR: "hour",
  SHOW_OTHER_BUILDINGS: "showOtherBuildings",
  DIMENSION_DISPLAY_TYPE: "dimensionDisplayType",
  BUILDINGS: "buildings",
  USAGES: "usages",
  FLOORS: "floors",
  ZONE_TYPE: "zoneTypes",
  FLOOR_SPREAD: "floorSpread",
  DAYS: "dow",
  PLAYBACK_SPEED_MODIFIER: "playbackSpeed",
  SEMESTER_METRIC: "semesterMetric",
  UTILIZATION_METRIC: "utilizationMetric",
  GRAPH_TYPE: "graph_type",
  RESOURCE_ID: "resourceId",
  HIDING_LEFT_PANE: "hidingLeftPane",
  HIDING_RIGHT_PANE: "hidingRightPane",
  CAMPUS_FLOW: "campusFlow",
  FLOW_THRESHOLD: "flowThreshold",
  FLOW_BUILDING: "flowBuilding",
  HIDING_OCCUPANCY: "hidingOccupancy",
}

// export const FLOOR_SPREAD_OPTIONS = [0, 1, 2, 3, 4, 5]
export const PLAYBACK_SPEED_MODIFIER_OPTIONS = [0.5, 1, 1.5, 2]

let DEFAULT_HOUR = 12

export const setDefaultHour = newHour => {
  DEFAULT_HOUR = newHour
  DEFAULT_FILTERS[FilterQueryArgs.HOUR] = newHour
}

export const DEFAULT_DIMENSION_DISPLAY_TYPE = DimensionDisplayType.TWO_D
export const DEFAULT_DAY_OPTIONS = [1, 2, 3, 4, 5]
export const DEFAULT_FLOOR_SPREAD = 0
export const DEFAULT_PLAYBACK_SPEED_MODIFIER = 1
export const DEFAULT_GRAPH_TYPE = GraphType.BY_DAY
export const DEFAULT_ZONE_TYPE = ZoneType.BUILDING
export const DEFAULT_FLOW_THRESHOLD = 75

const HOURLY_FILTER_KEYS = [
  FilterQueryArgs.HOUR,
  FilterQueryArgs.DAYS,
  FilterQueryArgs.GRAPH_TYPE,
  FilterQueryArgs.UTILIZATION_METRIC,
]

const DEFAULT_FILTERS = {
  [FilterQueryArgs.RESOURCE_ID]: null,
  [FilterQueryArgs.OPTION]: FilterIds.Options.SEMESTER,
  [FilterQueryArgs.HOUR]: DEFAULT_HOUR,
  [FilterQueryArgs.DIMENSION_DISPLAY_TYPE]: DEFAULT_DIMENSION_DISPLAY_TYPE,
  [FilterQueryArgs.BUILDINGS]: DEFAULT_ARRAY,
  [FilterQueryArgs.USAGES]: DEFAULT_ARRAY,
  [FilterQueryArgs.FLOORS]: DEFAULT_ARRAY,
  [FilterQueryArgs.ZONE_TYPE]: DEFAULT_ZONE_TYPE,
  [FilterQueryArgs.FLOOR_SPREAD]: DEFAULT_FLOOR_SPREAD,
  [FilterQueryArgs.DAYS]: DEFAULT_DAY_OPTIONS,
  [FilterQueryArgs.PLAYBACK_SPEED_MODIFIER]: DEFAULT_PLAYBACK_SPEED_MODIFIER,
  [FilterQueryArgs.SEMESTER_METRIC]: null,
  [FilterQueryArgs.UTILIZATION_METRIC]: null,
  [FilterQueryArgs.GRAPH_TYPE]: DEFAULT_GRAPH_TYPE,
  [FilterQueryArgs.CAMPUS_FLOW]: false,
  [FilterQueryArgs.FLOW_THRESHOLD]: DEFAULT_FLOW_THRESHOLD,
  [FilterQueryArgs.FLOW_BUILDING]: null,
  [FilterQueryArgs.HIDING_OCCUPANCY]: false,
}

const getSearch = (searchParams, key, options) => {
  const { defaultValue: ogDefaultValue, formatter } = options || {}
  let defaultValue = ogDefaultValue

  if (defaultValue == null) {
    // force undefined
    defaultValue = null
  }

  let value = searchParams.get(key)
  if (value == null || value === "") {
    return defaultValue
  }

  if (formatter) {
    value = formatter(value)
  }

  return value
}

const getSearchArrayKey = (searchParams, key) => {
  const value = searchParams.getAll(key)
  if (!value.length) {
    return null
  }

  return JSON.stringify(value.sort())
}

/**
 * @typedef {Object} FilterQueryParamsResult
 * @property {string|null} resourceId - The currently selected resource ID from URL params
 * @property {(newResourceId: string|null) => void} setResourceId - Sets the selected resource ID in URL params
 * @property {boolean} isHidingLeftPane - Boolean indicating if the left pane is hidden
 * @property {() => void} toggleLeftPane - Toggles the visibility of the left pane
 * @property {boolean} isHidingRightPane - Boolean indicating if the right pane is hidden
 * @property {() => void} toggleRightPane - Toggles the visibility of the right pane
 * @property {string} mapDisplayType - The current map display type (semester, hourly, heatmap, etc.)
 * @property {(filter: string) => void} onMapDisplayTypeChange - Changes the map display type
 * @property {boolean} showOtherBuildings - Boolean indicating if other buildings should be shown
 * @property {Object} filters - Object containing all current filter values
 * @property {boolean} hasFilters - Boolean indicating if any non-default filters are applied
 * @property {() => void} clearFilters - Resets all filters to their default values
 * @property {(filters: Object) => void} setFilters - Updates multiple filter values at once
 * @property {number} hour - The selected hour for hourly data display
 * @property {string} dimensionDisplayType - The dimension display type (2D or 3D)
 * @property {Array<string>} buildings - Array of selected building IDs
 * @property {Array<string>} usages - Array of selected usage type IDs
 * @property {Array<string>} floors - Array of selected floor IDs
 * @property {string} zoneType - selected zone type name
 * @property {number} floorSpread - The floor spread value for visualization
 * @property {Array<number>} days - Array of selected day numbers (1-7)
 * @property {number} playbackSpeedModifier - Speed modifier for playback controls
 * @property {string|null} semesterMetric - The selected semester metric column
 * @property {string|null} utilizationMetric - The selected utilization metric column
 * @property {string} graphType - The graph type for data visualization
 * @property {boolean} campusFlow - Whether Campus Flow visualization is enabled
 * @property {number} flowThreshold - Threshold percentile for flow route filtering (0-95, step 5, default 75)
 * @property {string|null} flowBuilding - Currently selected building ID for flow filtering, or null
 * @property {(newFlowBuilding: string|null) => void} setFlowBuilding - Sets the selected flow building ID in URL params
 * @property {boolean} isHidingOccupancy - Boolean indicating if building occupancy coloring is hidden
 * @property {() => void} toggleOccupancy - Toggles the visibility of building occupancy coloring
 */

/**
 * Custom hook for managing map filter and state query parameters.
 * Provides access to all URL-based state including resource selection, pane visibility,
 * map filters, and display settings. Automatically syncs state with URL parameters.
 * Does not automatically load data - components using this hook should handle data loading.
 *
 * @returns {FilterQueryParamsResult} Hook result object with all query params, state, and setter functions
 */
export const useFilterQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // create and store setSearchParams in a ref. i don't care about memo's getting broken when that changes
  const setSearchParamsRef = useRef(setSearchParams)
  if (setSearchParamsRef.current !== setSearchParams) {
    setSearchParamsRef.current = setSearchParams
  }

  const { semester } = useAppContext()

  // State query params (from useStateQueryParams)
  const resourceId = searchParams.get(FilterQueryArgs.RESOURCE_ID)

  // use in callbacks/memos that shouldn't require reloads based on this
  const resourceIdRef = useRef(resourceId)
  if (resourceIdRef.current !== resourceId) {
    resourceIdRef.current = resourceId
  }

  const hidingLeftPane = searchParams.get(FilterQueryArgs.HIDING_LEFT_PANE) === "true"
  const hidingRightPane = searchParams.get(FilterQueryArgs.HIDING_RIGHT_PANE) === "true"

  const clearFilters = useCallback(() => {
    setSearchParamsRef.current(old => {
      return copyUrlSearchParams(old, { keysToSkip: Object.keys(DEFAULT_FILTERS) })
    })
  }, [])

  const setFilters = useCallback((filters, options = {}) => {
    setSearchParamsRef.current(old => {
      // Combine filter keys with any additional keys to skip
      const keysToSkip = [...Object.keys(filters), ...(options.additionalKeysToSkip || [])]
      const data = copyUrlSearchParams(old, { keysToSkip })

      for (const [key, baseValue] of Object.entries(filters)) {
        let value = baseValue
        const defaultValue = DEFAULT_FILTERS[key]

        // Skip unknown keys not in DEFAULT_FILTERS
        if (!(key in DEFAULT_FILTERS)) {
          continue
        }

        // Both null/undefined → value matches default, skip
        if (value == null && defaultValue == null) {
          continue
        }

        // Non-null default strict match → skip
        if (defaultValue != null && defaultValue === value) {
          continue
        }

        // Array comparison (non-mutating sort)
        if (Array.isArray(value) && Array.isArray(defaultValue)) {
          value = [...value].sort()
          if (arrayEquals([...defaultValue].sort(), value)) {
            continue
          }
        }

        data[key] = value
      }

      return data
    })
  }, [])

  const setResourceId = useCallback(newResourceId => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.RESOURCE_ID] })

      // deselecting resource
      if (newResourceId == null || resourceIdRef.current === newResourceId) {
        return data
      }

      data[FilterQueryArgs.RESOURCE_ID] = newResourceId

      return data
    })
  }, [])

  const mapDisplayType = getSearch(searchParams, FilterQueryArgs.OPTION, {
    defaultValue: FilterIds.Options.SEMESTER,
  })

  const buildingsKey = getSearchArrayKey(searchParams, FilterQueryArgs.BUILDINGS)
  const [buildings, setBuildings] = useState(() => [])
  useEffect(() => {
    const newValue = searchParams.getAll(FilterQueryArgs.BUILDINGS)
    if (!arrayEquals(newValue, buildings)) {
      setBuildings(() => newValue)
    }
  }, [buildingsKey, buildings, searchParams])

  const usagesKey = getSearchArrayKey(searchParams, FilterQueryArgs.USAGES)
  const [usages, setUsages] = useState(() => {
    return searchParams.getAll(FilterQueryArgs.USAGES)
  })
  useEffect(() => {
    const newValue = searchParams.getAll(FilterQueryArgs.USAGES)
    if (!arrayEquals(newValue, usages)) {
      setUsages(newValue)
    }
  }, [searchParams, usages, usagesKey])

  const floorsKey = getSearchArrayKey(searchParams, FilterQueryArgs.FLOORS)
  const [floors, setFloors] = useState(() => [])
  useEffect(() => {
    const newValue = searchParams.getAll(FilterQueryArgs.FLOORS)
    if (!arrayEquals(newValue, floors)) {
      setFloors(newValue)
    }
  }, [floors, floorsKey, searchParams])

  const floorSpread = getSearch(searchParams, FilterQueryArgs.FLOOR_SPREAD, {
    defaultValue: DEFAULT_FLOOR_SPREAD,
    formatter: parseInt,
  })

  const dayKey = getSearchArrayKey(searchParams, FilterQueryArgs.DAYS)
  const [days, setDays] = useState(() => [])
  useEffect(() => {
    const getValue = () => {
      const current = searchParams.getAll(FilterQueryArgs.DAYS).map(day => parseInt(day))
      if (current.length) {
        return current
      }

      return DEFAULT_DAY_OPTIONS
    }

    const newValue = getValue()
    if (!arrayEquals(newValue, days)) {
      setDays(() => newValue)
    }
  }, [dayKey, days, searchParams])

  const hour = getSearch(searchParams, FilterQueryArgs.HOUR, {
    defaultValue: DEFAULT_HOUR,
    formatter: parseInt,
  })

  useEffect(() => {
    if (semester?.local_hour == null || semester?.local_hour === DEFAULT_HOUR) {
      return
    }

    setDefaultHour(semester?.local_hour)

    setFilters({ [FilterQueryArgs.HOUR]: semester?.local_hour })
  }, [semester?.local_hour, setFilters])

  const zoneType = getSearch(searchParams, FilterQueryArgs.ZONE_TYPE, {
    defaultValue: DEFAULT_ZONE_TYPE,
  })
  const showOtherBuildings = searchParams.get(FilterQueryArgs.SHOW_OTHER_BUILDINGS) == null
  const dimensionDisplayType = getSearch(searchParams, FilterQueryArgs.DIMENSION_DISPLAY_TYPE, {
    defaultValue: DEFAULT_DIMENSION_DISPLAY_TYPE,
  })
  const playbackSpeedModifier = getSearch(searchParams, FilterQueryArgs.PLAYBACK_SPEED_MODIFIER, {
    defaultValue: DEFAULT_PLAYBACK_SPEED_MODIFIER,
    formatter: parseFloat,
  })

  const semesterMetric = getSearch(searchParams, FilterQueryArgs.SEMESTER_METRIC, {
    defaultValue: DEFAULT_FILTERS[FilterQueryArgs.SEMESTER_METRIC],
  })
  const utilizationMetric = getSearch(searchParams, FilterQueryArgs.UTILIZATION_METRIC, {
    defaultValue: DEFAULT_FILTERS[FilterQueryArgs.UTILIZATION_METRIC],
  })
  const graphType = getSearch(searchParams, FilterQueryArgs.GRAPH_TYPE, {
    defaultValue: DEFAULT_GRAPH_TYPE,
  })

  const campusFlow = searchParams.get(FilterQueryArgs.CAMPUS_FLOW) === "true"
  const rawFlowThreshold = getSearch(searchParams, FilterQueryArgs.FLOW_THRESHOLD, {
    defaultValue: DEFAULT_FLOW_THRESHOLD,
    formatter: parseInt,
  })
  const flowThreshold = Number.isNaN(rawFlowThreshold)
    ? DEFAULT_FLOW_THRESHOLD
    : Math.max(0, Math.min(95, rawFlowThreshold))
  const hidingOccupancy = searchParams.get(FilterQueryArgs.HIDING_OCCUPANCY) === "true"

  const flowBuilding = searchParams.get(FilterQueryArgs.FLOW_BUILDING)

  const flowBuildingRef = useRef(flowBuilding)
  if (flowBuildingRef.current !== flowBuilding) {
    flowBuildingRef.current = flowBuilding
  }

  const setFlowBuilding = useCallback(newFlowBuilding => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.FLOW_BUILDING] })

      if (newFlowBuilding == null || flowBuildingRef.current === newFlowBuilding) {
        return data
      }

      data[FilterQueryArgs.FLOW_BUILDING] = newFlowBuilding

      return data
    })
  }, [])

  const currentFilters = useMemo(() => {
    return {
      [FilterQueryArgs.RESOURCE_ID]: resourceId,
      [FilterQueryArgs.HOUR]: hour,
      [FilterQueryArgs.BUILDINGS]: buildings,
      [FilterQueryArgs.USAGES]: usages,
      [FilterQueryArgs.FLOORS]: floors,
      [FilterQueryArgs.ZONE_TYPE]: zoneType,
      [FilterQueryArgs.FLOOR_SPREAD]: floorSpread,
      [FilterQueryArgs.DAYS]: days,
      [FilterQueryArgs.PLAYBACK_SPEED_MODIFIER]: playbackSpeedModifier,
      [FilterQueryArgs.SEMESTER_METRIC]: semesterMetric,
      [FilterQueryArgs.UTILIZATION_METRIC]: utilizationMetric,
      [FilterQueryArgs.GRAPH_TYPE]: graphType,
      [FilterQueryArgs.CAMPUS_FLOW]: campusFlow,
      [FilterQueryArgs.FLOW_THRESHOLD]: flowThreshold,
      [FilterQueryArgs.FLOW_BUILDING]: flowBuilding,
      [FilterQueryArgs.HIDING_OCCUPANCY]: hidingOccupancy,
    }
  }, [
    resourceId,
    hour,
    buildings,
    usages,
    floors,
    zoneType,
    floorSpread,
    days,
    playbackSpeedModifier,
    semesterMetric,
    utilizationMetric,
    graphType,
    campusFlow,
    flowThreshold,
    flowBuilding,
    hidingOccupancy,
  ])

  const toggleLeftPane = useCallback(() => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.HIDING_LEFT_PANE] })

      // Check current state to determine new state
      const isCurrentlyHiding = old.get(FilterQueryArgs.HIDING_LEFT_PANE) === "true"

      if (!isCurrentlyHiding) {
        // Currently visible → hide it
        data[FilterQueryArgs.HIDING_LEFT_PANE] = true
      }
      // If currently hiding, param is already removed by copyUrlSearchParams

      return data
    })
  }, [])

  const toggleRightPane = useCallback(() => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.HIDING_RIGHT_PANE] })

      // Check current state to determine new state
      const isCurrentlyHiding = old.get(FilterQueryArgs.HIDING_RIGHT_PANE) === "true"

      if (!isCurrentlyHiding) {
        // Currently visible → hide it
        data[FilterQueryArgs.HIDING_RIGHT_PANE] = true
      }
      // If currently hiding, param is already removed by copyUrlSearchParams

      return data
    })
  }, [])

  const toggleOccupancy = useCallback(() => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.HIDING_OCCUPANCY] })

      const isCurrentlyHiding = old.get(FilterQueryArgs.HIDING_OCCUPANCY) === "true"

      if (!isCurrentlyHiding) {
        data[FilterQueryArgs.HIDING_OCCUPANCY] = true
      }

      return data
    })
  }, [])

  const onMapDisplayTypeChange = useCallback(
    filter => {
      if (filter === mapDisplayType) {
        return
      }

      setSearchParamsRef.current(old => {
        const data = copyUrlSearchParams(old, { keysToSkip: [FilterQueryArgs.OPTION, ...HOURLY_FILTER_KEYS] })

        if (filter && filter !== FilterIds.Options.SEMESTER) {
          data[FilterQueryArgs.OPTION] = filter
        }

        return data
      })
    },
    [mapDisplayType]
  )

  return {
    // State query params
    resourceId,
    setResourceId,

    isHidingLeftPane: hidingLeftPane,
    toggleLeftPane,

    isHidingRightPane: hidingRightPane,
    toggleRightPane,

    // Filter query params
    mapDisplayType,
    onMapDisplayTypeChange,

    showOtherBuildings,

    filters: currentFilters,
    hasFilters: !objectEqual(DEFAULT_FILTERS, currentFilters),
    clearFilters,
    setFilters,

    hour,
    dimensionDisplayType,
    buildings,
    usages,
    floors,
    zoneType,
    floorSpread,
    days,
    playbackSpeedModifier,
    semesterMetric,
    utilizationMetric,
    graphType,

    // Campus Flow params
    campusFlow,
    flowThreshold,
    flowBuilding,
    setFlowBuilding,

    // Occupancy visibility
    isHidingOccupancy: hidingOccupancy,
    toggleOccupancy,
  }
}
